// episodeOfCare/index.jsx
// Based on TMS_Protocols

import * as React from 'react';
import '../../../index.css';
import { useEffect, useState, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import DataEntry from '../../../components/datagrid/dataEntry';
import ViewOnly from '../../../components/datagrid/viewOnly';
import UserContext from '../../../contexts/UserContext';
import { getData, postData, putData } from '../../../utils/API';
import {
  validateRequiredAttributes,
  validateDateDefined,
  validateDateNotDefined,
  validateEndDateGreaterThanStartDate,
  validateNotFutureDate,
  typeOfDate,
} from '../../../utils/ValidationUtils';
import ShowAlert from '../../../utils/ShowAlert';
import { useNotificationHandling } from '../../../utils/NotificationHandling';
import logger from '../../../utils/logger';
import { calculateAge } from '../../../utils/DateUtils';
import { CleaningServices } from '@mui/icons-material';

// *************** CUSTOMIZE **************
export default function EpisodesDataGrid() {
  const { notificationState, handleErrorNotification, handleClose } =
    useNotificationHandling();

  const navigate = useNavigate();
  const location = useLocation();

  const { patient } = location.state;
  //logger.info(`episodesDataGrid EpisodeOfCare patient is ${patient}`);
  const patientId = patient['id'];
  const patient_name = patient['full_name'];
  const lastName = patient['last_name'];
  const firstName = patient['first_name'];
  const birthdate = patient['birthdate'];
  const { role, practiceId } = useContext(UserContext);

  const title = patient_name;
  const subtitle = 'Episodes of Care';

  const table = 'episodes_of_care';

  function createRowData(rows) {
    const newId = Math.floor(100000 + Math.random() * 900000);
    return {
      id: newId,
      office_name: '',
      practitioner_full_name: '',
      status: 'In Progress',
      start_date: '',
      end_date: '',
      age: '',
    };
  }

  // *************** CUSTOMIZE **************

  const [loading, setLoading] = useState(true);
  const [rows, setRawRows] = useState([]);

  const setRows = (rows) => {
    if (!Array.isArray(rows)) {
      return;
    }
    setRawRows(rows.map((r, i) => ({ ...r, no: i + 1 })));
  };

  const query_params = {
    patient_id: patientId,
    deleted: false,
  };

  useEffect(() => {
    setLoading(true);
    getData('episodes_of_care', query_params)
      .then((data) => {
        setRows(data);
      })
      .catch((error) => {
        handleErrorNotification(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [patientId, handleErrorNotification]);

  const query_params_ref_data = { deleted: false, status: 'Active' };

  const [officeObjects, setOfficeObjects] = useState([]);
  const [officeNames, setOfficeNames] = useState([]);

  useEffect(() => {
    setLoading(true);
    getData('practice_offices', query_params_ref_data)
      .then((data) => {
        setOfficeObjects(data);
        const names = data.map((office) => office.name);
        setOfficeNames(names);
      })
      .catch((error) => {
        handleErrorNotification(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [handleErrorNotification]);

  const [practitionerObjects, setPractitionerObjects] = useState([]);
  const [practitionerNames, setPractitionerNames] = useState([]);

  useEffect(() => {
    setLoading(true);
    getData('practice_practitioners', query_params_ref_data)
      .then((data) => {
        setPractitionerObjects(data);
        const names = data.map((practitioner) => practitioner.full_name);
        setPractitionerNames(names);
      })
      .catch((error) => {
        handleErrorNotification(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [handleErrorNotification]);

  // Handle row click
  const handleRowClick = (params) => {
    const currentRow = { ...params.row };

    const startDate = typeOfDate(currentRow.start_date, 'string');
    currentRow.start_date = startDate;

    if (
      typeof currentRow.end_date !== 'undefined' &&
      currentRow.end_date !== null
    ) {
      const endDate = typeOfDate(currentRow.end_date, 'string');
      currentRow.end_date = endDate;
    } else {
      currentRow.endDate = ' ';
    }

    currentRow.lastName = lastName;
    currentRow.firstName = firstName;

    navigate('/patient/episode/dashboard', {
      state: { episodeOfCare: currentRow },
    });
  };

  const columns = [
    {
      field: 'office_name',
      headerName: 'Office',
      type: 'singleSelect',
      valueOptions: officeNames,
      editable: true,
      flex: 0.5,
      defaultValue: '',
    },
    {
      field: 'practitioner_full_name',
      headerName: 'Practitioner',
      type: 'singleSelect',
      valueOptions: practitionerNames,
      editable: true,
      flex: 0.5,
    },
    {
      field: 'status',
      headerName: 'Status',
      type: 'singleSelect',
      valueOptions: ['In Progress', 'Completed', 'Discontinued', 'Terminated'],
      editable: true,
      flex: 0.5,
    },
    {
      field: 'start_date',
      headerName: 'Start date',
      type: 'date',
      width: 180,
      editable: true,
      valueGetter: (params) => {
        // On the first render start_date is a string, on subsequent renders its a date object
        const startDate = params.row.start_date;
        const startDateObject = typeOfDate(startDate, 'object');
        return startDateObject;
      },
    },
    {
      field: 'end_date',
      headerName: 'End date',
      type: 'date',
      width: 180,
      editable: true,
      valueGetter: (params) => {
        // BE CAREFUL CHANGING THIS - WAS DIFFICULT TO MAKE WORK
        // ACROSS ALL USE CASES
        const endDate = params.row.end_date;
        if (endDate instanceof Date) {
          if (!isNaN(Date.parse(endDate))) {
            return endDate;
          } else {
            return '';
          }
        }

        if (typeof endDate === 'string') {
          const endDateString = endDate.replace(/-/g, '/');
          const endDateObject = new Date(endDateString);
          if (!isNaN(endDateObject.getTime())) {
            return endDateObject;
          } else {
            return;
          }
        }
      },
    },
    {
      field: 'age',
      headerName: 'Patient Age',
      headerAlign: 'center',
      align: 'center',
      valueFormatter: ({ value }) => value.toLocaleString(),
      flex: 1,
    },
  ];

  async function validateRow(newRow, oldRow) {
    try {
      const requiredAttributes = ['office_name', 'practitioner_full_name'];
      const attributeNames = ['Office', 'Practitioner'];
      validateRequiredAttributes(requiredAttributes, attributeNames, newRow);
      validateDateDefined(newRow['start_date'], 'Start Date');
      validateNotFutureDate(newRow['start_date'], 'Start Date');

      if (newRow.status === 'In Progress') {
        validateDateNotDefined(newRow['end_date'], 'End Date');
      } else {
        validateDateDefined(newRow['end_date'], 'End Date');
        validateEndDateGreaterThanStartDate(
          newRow['start_date'],
          newRow['end_date']
        );
        validateNotFutureDate(newRow['end_date'], 'End Date');
      }

      const episodeInProgress = episodesOfCareInProgressExists(rows);
      if (episodeInProgress === true) {
        const customError = new Error();
        customError.name = 'Data Validation Error';
        customError.message = `A patient can only have one episode of care with the status of In Progress.`;
        throw customError;
      }
      return newRow;
    } catch (error) {
      throw error;
    }
  }

  function getObjectID(
    newRow,
    oldRow,
    keyToCompare,
    objectsArray,
    matchingKey
  ) {
    const object = objectsArray.find(
      (obj) => obj[matchingKey] === newRow[keyToCompare]
    );
    if (object) {
      return object.id;
    } else {
      const customError = new Error();
      customError.name = 'Data Validation Error';
      customError.message = `${keyToCompare} not found.`;
      throw customError;
    }
  }

  async function saveRow(id, row, oldRow, oldRows) {
    try {
      const rowToSave = { ...row };

      const startDateString = typeOfDate(row.start_date, 'string');
      rowToSave.start_date = startDateString;

      if (rowToSave.status === 'In Progress') {
        delete rowToSave.end_date;
      } else {
        const endDateString = typeOfDate(rowToSave.end_date, 'string');
        rowToSave.end_date = endDateString;
      }

      const officeId = getObjectID(
        rowToSave,
        oldRow,
        'office_name',
        officeObjects,
        'name'
      );
      rowToSave.office_id = officeId;

      const practitionerId = getObjectID(
        rowToSave,
        oldRow,
        'practitioner_full_name',
        practitionerObjects,
        'full_name'
      );
      rowToSave.practitioner_id = practitionerId;

      /*
      let age = 0;
      if (birthdate) {
        logger.info(`Birthdate: ${birthdate}`);
        logger.info(`Start date: ${row.start_date}`);
        age = calculateAge(row.start_date, birthdate);
        logger.info(`Calculated age: ${age}`);
        rowToSave['age'] = age;
      } else {
        rowToSave['age'] = 0;
      }
      */

      if (rowToSave.isNew) {
        delete rowToSave.id;
        rowToSave.practice_id = practiceId;
        rowToSave.patient_id = patientId;
        rowToSave.deleted = false;

        logger.info(`rowToSave: ${rowToSave}`);
        const data = await postData(table, rowToSave);
        rowToSave.id = data.data.id;

        // Use the original date object
        rowToSave.start_date = row.start_date;

        // Get duplicate key error without this
        rowToSave.isNew = false;

        // Add last and first name
        rowToSave.last_name = lastName;
        rowToSave.first_name = firstName;

        setRows(oldRows.map((r) => (r.id === id ? { ...rowToSave } : r)));
        return rowToSave;
      } else {
        await putData(table, rowToSave);
        rowToSave.start_date = row.startDate;
        if ('endDate' in rowToSave) {
          rowToSave.end_date = row.end_date;
        }

        setRows(oldRows.map((r) => (r.id === id ? { ...row } : r)));
        return rowToSave;
      }
    } catch (error) {
      setRows(oldRows);
      throw error;
    }
  }

  async function episodesTestScoreExists(row) {
    try {
      const query_params = { episodes_of_care_id: row.id, deleted: false };
      const result = await getData(
        'episode_questionnaire_scores',
        query_params
      );
      if (result.length === 0) {
        return false;
      } else {
        return true;
      }
    } catch (error) {
      return true;
    }
  }

  async function episodesOfCareInProgressExists(rows) {
    let countInProgress = 0;
    rows.forEach((row, index) => {
      if (row['status'] === 'In Progress') {
        countInProgress++;
      }
    });
    if (countInProgress > 1) {
      return true;
    } else {
      return false;
    }
  }

  async function deleteRow(id, row, oldRows) {
    // Need to create an error object for this condition
    const testScoreExists = await episodesTestScoreExists(row);
    if (testScoreExists) {
      setRows(oldRows);
      const customError = new Error();
      customError.name = 'Delete Error';
      customError.message = `This Episode of Care has test scores. Delete the test scores before deleting the Episode of Care.`;
      throw customError;
    }

    const rowToSave = { ...row, deleted: true };
    delete rowToSave.start_date;
    delete rowToSave.end_date;

    try {
      await putData(table, rowToSave);
      setRows(oldRows.filter((r) => r.id !== id));
      return 'Deleted';
    } catch (error) {
      setRows(oldRows);
      throw error;
    }
  }

  if (notificationState.showNotification) {
    return (
      <ShowAlert
        severity={notificationState.severity}
        title={notificationState.title}
        message={notificationState.message}
        description={notificationState.description}
        onClose={handleClose}
      />
    );
  }

  if (
    role === 'manager' ||
    role === 'admin' ||
    role === 'super' ||
    role === 'user'
  ) {
    return (
      <div>
        <DataEntry
          title={title}
          subtitle={subtitle}
          columns={columns}
          rows={rows}
          onRowClick={handleRowClick}
          onValidateRow={validateRow}
          onSaveRow={saveRow}
          onDeleteRow={deleteRow}
          createRowData={createRowData}
          loading={loading}
        />
      </div>
    );
  } else {
    return (
      <div>
        <ViewOnly
          title={title}
          subtitle={subtitle}
          columns={columns}
          rows={rows}
        />
      </div>
    );
  }
}
